// Sprint 0081 — Employee skill / trade matrix admin page.
// Lists all skills with filters, allows add/edit, supports bulk import.

import { useState } from 'react'
import {
  App, Button, DatePicker, Form, Input, InputNumber, Modal, Select,
  Space, Tag, Tooltip, Typography, Upload,
} from 'antd'
import {
  CheckCircleOutlined, EditOutlined, PlusOutlined, UploadOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  hrImportApi, skillsApi, TRADE_TYPES, SKILL_CATEGORIES, CERT_STATUSES,
  SKILL_LEVEL_LABELS,
  type Skill, type SkillCategory, type TradeType, type CertStatus,
  type ImportPreview,
} from '@/features/hr/enablementApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { employeesApi } from '@/features/hr/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text } = Typography

const MACHINE_TYPES = [
  'BLOW_ROOM', 'CARDING', 'COMBER', 'DRAW_FRAME_BREAKER', 'DRAW_FRAME_FINISHER',
  'LAP_FORMER', 'SIMPLEX', 'RING_FRAME', 'AUTO_CONER', 'YCP',
]

const certColor: Record<CertStatus, string> = {
  NOT_CERTIFIED: 'default',
  TRAINING: 'gold',
  CERTIFIED: 'green',
  EXPIRED: 'red',
  SUSPENDED: 'volcano',
}

export default function SkillMatrixPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')

  // Filters
  const [deptFilter, setDeptFilter] = useState<string | undefined>()
  const [tradeFilter, setTradeFilter] = useState<TradeType | undefined>()
  const [categoryFilter, setCategoryFilter] = useState<SkillCategory | undefined>()
  const [levelMin, setLevelMin] = useState<number | undefined>()
  const [certFilter, setCertFilter] = useState<CertStatus | undefined>()
  const [machineTypeFilter, setMachineTypeFilter] = useState<string | undefined>()

  // CRUD modal
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Skill | null>(null)
  const [form] = Form.useForm()

  // Import
  const [importOpen, setImportOpen] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null)

  const { data: depts } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })

  const { data: employees } = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => employeesApi.list({ page: 1, size: 1000 }),
  })

  const { data: skills, isLoading } = useQuery({
    queryKey: [
      'skills', deptFilter, tradeFilter, categoryFilter, levelMin, certFilter, machineTypeFilter,
    ],
    queryFn: () => skillsApi.list({
      department_id: deptFilter,
      trade_type: tradeFilter,
      skill_category: categoryFilter,
      skill_level_min: levelMin,
      certification_status: certFilter,
      machine_type: machineTypeFilter,
    }),
  })

  const empName = (id: string) =>
    employees?.items.find((e) => e.id === id)?.emp_name ?? '—'
  const empCode = (id: string) =>
    employees?.items.find((e) => e.id === id)?.emp_code ?? '—'

  const onCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ skill_level: 0, certification_status: 'NOT_CERTIFIED' })
    setDrawerOpen(true)
  }
  const onEdit = (s: Skill) => {
    setEditing(s)
    form.setFieldsValue({
      ...s,
      certified_from: s.certified_from ? dayjs(s.certified_from) : null,
      certified_till: s.certified_till ? dayjs(s.certified_till) : null,
    })
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const dates = {
        certified_from: values.certified_from
          ? (values.certified_from as dayjs.Dayjs).format('YYYY-MM-DD') : null,
        certified_till: values.certified_till
          ? (values.certified_till as dayjs.Dayjs).format('YYYY-MM-DD') : null,
      }
      if (editing) {
        return skillsApi.update(editing.id, {
          skill_level: values.skill_level as number,
          certification_status: values.certification_status as CertStatus,
          remarks: (values.remarks as string) || null,
          ...dates,
        })
      }
      return skillsApi.create({
        employee_id: values.employee_id as string,
        skill_category: values.skill_category as SkillCategory,
        skill_name: values.skill_name as string,
        trade_type: values.trade_type as TradeType,
        machine_type: (values.machine_type as string) || null,
        skill_level: values.skill_level as number,
        certification_status: values.certification_status as CertStatus,
        remarks: (values.remarks as string) || null,
        ...dates,
      })
    },
    onSuccess: () => {
      message.success(editing ? 'Skill updated' : 'Skill added')
      setDrawerOpen(false)
      qc.invalidateQueries({ queryKey: ['skills'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    {
      title: 'Employee', key: 'employee_id', width: 200,
      render: (_: unknown, s: Skill) =>
        <div>
          <Text strong>{empCode(s.employee_id)}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{empName(s.employee_id)}</Text>
        </div>,
    },
    { title: 'Skill', dataIndex: 'skill_name', key: 'skill_name' },
    {
      title: 'Category', dataIndex: 'skill_category', key: 'skill_category', width: 160,
      render: (v: SkillCategory) => <Tag>{v}</Tag>,
    },
    {
      title: 'Trade', dataIndex: 'trade_type', key: 'trade_type', width: 130,
      render: (v: TradeType) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Machine Type', dataIndex: 'machine_type', key: 'machine_type', width: 130,
      render: (v?: string | null) => v ? <Tag color="purple">{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Level', dataIndex: 'skill_level', key: 'skill_level', width: 110,
      render: (v: number) => SKILL_LEVEL_LABELS[v] ?? v,
    },
    {
      title: 'Certification', dataIndex: 'certification_status', key: 'cert', width: 130,
      render: (v: CertStatus, r: Skill) => (
        <Space direction="vertical" size={0}>
          <Tag color={certColor[v]} icon={v === 'CERTIFIED' ? <CheckCircleOutlined /> : undefined}>
            {v}
          </Tag>
          {r.certified_till && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              till {dayjs(r.certified_till).format('DD MMM YYYY')}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 90,
      render: (_: unknown, r: Skill) => canEdit ? (
        <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)}>Edit</Button>
      ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Skill / Trade Matrix"
        subtitle="Who is certified for which trade and machine. Drives maintenance auto-assignment + future deployment guardrails."
        extra={
          <Space>
            {canEdit && (
              <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                Import Excel
              </Button>
            )}
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
                Add Skill
              </Button>
            )}
          </Space>
        }
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Select allowClear placeholder="Department" style={{ width: 180 }}
          options={(depts?.items ?? []).map((d) => ({ label: d.name, value: d.id }))}
          onChange={setDeptFilter} />
        <Select allowClear placeholder="Trade" style={{ width: 160 }}
          options={TRADE_TYPES.map((t) => ({ label: t, value: t }))}
          onChange={(v) => setTradeFilter(v as TradeType | undefined)} />
        <Select allowClear placeholder="Skill category" style={{ width: 200 }}
          options={SKILL_CATEGORIES.map((c) => ({ label: c, value: c }))}
          onChange={(v) => setCategoryFilter(v as SkillCategory | undefined)} />
        <Select allowClear placeholder="Min level" style={{ width: 140 }}
          options={[0, 1, 2, 3, 4, 5].map((n) => ({ label: SKILL_LEVEL_LABELS[n], value: n }))}
          onChange={(v) => setLevelMin(v as number | undefined)} />
        <Select allowClear placeholder="Certification" style={{ width: 160 }}
          options={CERT_STATUSES.map((c) => ({ label: c, value: c }))}
          onChange={(v) => setCertFilter(v as CertStatus | undefined)} />
        <Select allowClear placeholder="Machine type" style={{ width: 180 }}
          options={MACHINE_TYPES.map((m) => ({ label: m, value: m }))}
          onChange={setMachineTypeFilter} />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={skills ?? []}
        loading={isLoading}
      />

      {/* Add / Edit modal */}
      <Modal
        title={editing ? 'Edit skill' : 'Add skill'}
        open={drawerOpen}
        onCancel={() => setDrawerOpen(false)}
        onOk={onSubmit}
        confirmLoading={saveMutation.isPending}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="employee_id"
            label="Employee"
            rules={[{ required: true }]}
          >
            <Select
              disabled={!!editing}
              showSearch
              placeholder="Pick employee"
              options={(employees?.items ?? []).map((e) => ({
                label: `${e.emp_name} (${e.emp_code})`,
                value: e.id,
              }))}
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="skill_category" label="Skill Category" rules={[{ required: true }]}>
            <Select disabled={!!editing}
              options={SKILL_CATEGORIES.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
          <Form.Item name="skill_name" label="Skill Name" rules={[{ required: true }]}>
            <Input disabled={!!editing} placeholder="e.g. Bearing replacement on Carding" />
          </Form.Item>
          <Form.Item name="trade_type" label="Trade Type" rules={[{ required: true }]}>
            <Select disabled={!!editing}
              options={TRADE_TYPES.map((t) => ({ label: t, value: t }))} />
          </Form.Item>
          <Form.Item name="machine_type" label="Machine Type (optional)">
            <Select disabled={!!editing}
              allowClear
              options={MACHINE_TYPES.map((m) => ({ label: m, value: m }))} />
          </Form.Item>
          <Form.Item name="skill_level" label="Skill Level" rules={[{ required: true }]}>
            <Select
              options={[0, 1, 2, 3, 4, 5].map((n) => ({
                label: SKILL_LEVEL_LABELS[n], value: n,
              }))}
            />
          </Form.Item>
          <Form.Item name="certification_status" label="Certification Status" rules={[{ required: true }]}>
            <Select options={CERT_STATUSES.map((c) => ({ label: c, value: c }))} />
          </Form.Item>
          <Space>
            <Form.Item name="certified_from" label="Certified From">
              <DatePicker style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="certified_till" label="Certified Till">
              <DatePicker style={{ width: 200 }} />
            </Form.Item>
          </Space>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk skill import */}
      <Modal
        title="Bulk import skills"
        open={importOpen}
        onCancel={() => { setImportOpen(false); setImportFile(null); setImportPreview(null) }}
        width={900}
        footer={
          <Space>
            <Button onClick={() => { setImportOpen(false); setImportFile(null); setImportPreview(null) }}>
              Close
            </Button>
            {importFile && !importPreview && (
              <Button type="primary" onClick={async () => {
                try {
                  const p = await hrImportApi.previewSkills(importFile)
                  setImportPreview(p)
                } catch { message.error('Preview failed') }
              }}>
                Preview
              </Button>
            )}
            {importPreview && importPreview.error < importPreview.total && (
              <Button type="primary" onClick={async () => {
                if (!importFile) return
                try {
                  const r = await hrImportApi.commitSkills(importFile)
                  message.success(`Imported: ${r.created}, skipped: ${r.skipped}`)
                  setImportOpen(false); setImportFile(null); setImportPreview(null)
                  qc.invalidateQueries({ queryKey: ['skills'] })
                } catch { message.error('Import failed') }
              }}>
                Commit {importPreview.ok} rows
              </Button>
            )}
          </Space>
        }
      >
        <Typography.Paragraph>
          <Button
            type="link"
            icon={<UploadOutlined rotate={180} />}
            href="/skill_matrix_template.xlsx"
            target="_blank"
          >
            Download Excel template
          </Button>
          <Typography.Text type="secondary"> — pre-filled with examples and all valid values for skill_category, trade_type, machine_type, certification_status.</Typography.Text>
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          Columns: <code>emp_code, skill_category, skill_name, trade_type, machine_type,
          skill_level, certification_status, certified_from, certified_till, remarks</code>.
          Employee must exist in master.
        </Typography.Paragraph>
        <Upload
          beforeUpload={(f) => { setImportFile(f); setImportPreview(null); return false }}
          maxCount={1} accept=".xlsx,.xls,.csv"
        >
          <Button icon={<UploadOutlined />}>Select file</Button>
        </Upload>
        {importFile && (
          <Typography.Text style={{ display: 'block', marginTop: 8 }}>
            Selected: <strong>{importFile.name}</strong>
          </Typography.Text>
        )}
        {importPreview && (
          <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }}>
              <Tag color="green">{importPreview.ok} ok</Tag>
              <Tag color="red">{importPreview.error} error</Tag>
              <Tag>{importPreview.total} total</Tag>
            </Space>
            <DataTable
              rowKey={(r: { row_no: number }) => `r${r.row_no}`}
              size="small"
              pagination={{ pageSize: 10 }}
              dataSource={importPreview.rows}
              columns={[
                { title: 'Row', dataIndex: 'row_no', key: 'row_no', width: 60 },
                {
                  title: 'Status', dataIndex: 'status', key: 'status', width: 90,
                  render: (s: string) =>
                    <Tag color={s === 'OK' ? 'green' : 'red'}>{s}</Tag>,
                },
                {
                  title: 'Emp code', key: 'emp_code',
                  render: (_: unknown, r: unknown) =>
                    (r as { data: { emp_code: string } }).data.emp_code,
                },
                {
                  title: 'Skill', key: 'skill_name',
                  render: (_: unknown, r: unknown) =>
                    (r as { data: { skill_name: string } }).data.skill_name,
                },
                {
                  title: 'Errors', key: 'errors',
                  render: (_: unknown, r: unknown) => {
                    const errs = (r as { errors: string[] }).errors
                    return errs?.length
                      ? <Text type="danger">{errs.join(' · ')}</Text>
                      : <Text type="secondary">—</Text>
                  },
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  )
}
